import type { ModuleConfig } from "../moduleConfig.types";
import {
  BRANDS,
  MARKETS,
  FILTERS,
  SEGMENTS,
  PERIOD_TYPE,
  PERIOD,
  POST_PERIOD_TYPE,
  POST_START_DATE,
  POST_END_DATE,
  STAT_TEST,
  BASE,
  MODE,
  PERIOD_START_DATE,
  PERIOD_END_DATE,
  VISUALIZATION_TYPE,
  AGE_START,
  AGE_END,
  METRIC,
  RESPONSE,
  PROFILE_GROUP,
  PROFILE_METRIC,
  PROFILE_RESPONSE,
  TRENDS_METRIC,
  TRENDS_RESPONSE,
  VIEW_ID,
} from "@/constant/constantsThrougoutProject";

/**
 * Hierarchical screen config for the Advertising module.
 *
 * Resolution order (later layers override earlier ones):
 *   module.config
 *   → screenTypes.config
 *   → screenTypes.[name].config
 *   → screenTypes.[name].modes.config
 *   → screenTypes.[name].modes.[name].config
 *   → screenTypes.[name].modes.[name].visualisationType.config
 *   → screenTypes.[name].modes.[name].visualisationType.[name].config
 */

export const advertisingModuleConfig: ModuleConfig = {
  // ── Module-level base config ──────────────────────────────────────────────
  config: {
    type: "MODULE",
    module: "ADVERTISING",
    supportMarkets: true,
    isMarketsFilteredByBrand: false,
    minimumRequiredMarkets: 1,
    supportBrands: true,
    isBrandsFilteredByMarket: true,
    supportSecondaryBrands: false,
    supportMode: true,
    defaultMode: "POINT_IN_TIME",
    minimumRequiredBrands: 1,
    supportedModes: ["TREND", "POINT_IN_TIME", "PRE_POST"],
    supportBase: true,
    defaultBase: "GENPOP",
    supportedBases: ["GENPOP", "FAMILIAR"],
    supportDatePeriod: true,
    supportScoreType: false,
    supportVisualisationSwitch: true,
    supportedVisualisationTypes: ["DONUT", "BAR", "PROFILE"],
    defaultVisualisationType: "DONUT",
    supportStatTesting: true,
  },

  screenTypes: {
    // ── By Segment ───────────────────────────────────────────────────────────
    SEGMENT: {
      config: {
        type: "SCREEN_TYPE",
        segmentFilterModalMode: "SEGMENT_ONLY",
        maximumAllowedMarkets: 1,
        maximumAllowedBrands: 1,
        apiGroupedBy: "SEGMENT",
        apiSendFilters: false,
      },
      modes: {
        TREND: {
          config: {
            apiViewMode: "TREND",
            apiSendIntervalType: true,
            supportPeriod: true,
            supportedPeriod: [
              "P_W",
              "P_M",
              "P_Q",
              "MV_1",
              "MV_2",
              "MV_4",
              "MV_8",
              "MV_12",
            ],
            supportedURLKeys: [
              MARKETS,
              BRANDS,
              SEGMENTS,
              STAT_TEST,
              BASE,
              MODE,
              PERIOD_TYPE,
              PERIOD_START_DATE,
              PERIOD_END_DATE,
              PERIOD,
              VISUALIZATION_TYPE,
              TRENDS_METRIC,
              TRENDS_RESPONSE,
              VIEW_ID,
            ],
            supportTrendsMetric: true,
            supportedTrendsMetric: [
              {
                value: "adRecall",
                label: "Advertising Recall",
                disablesBases: ["ADRECALL", "USER"],
              },
              {
                value: "tvCableSatelliteBroadcast",
                label: "TV (cable/satellite/broadcast)",
                disablesBases: ["ADRECALL", "USER"],
              },
              {
                value: "onlineStreamingVideo",
                label: "Online/Streaming Video",
                disablesBases: ["ADRECALL", "USER"],
              },
              {
                value: "newsEntertainmentWebsiteOrApp",
                label: "News/Entertainment Website or App",
                disablesBases: ["ADRECALL", "USER"],
              },
              {
                value: "eCommerceReviewWebsiteOrApp",
                label: "E-commerce/Review Website or App",
                disablesBases: ["ADRECALL", "USER"],
              },
              {
                value: "postPopUpOnSocialMedia",
                label: "Post/Pop-up on Social Media",
                disablesBases: ["ADRECALL", "USER"],
              },
              {
                value: "influencerOnSocialMedia",
                label: "Influencer on Social Media",
                disablesBases: ["ADRECALL", "USER"],
              },
              {
                value: "storePhysicalLocation",
                label: "Store/Physical Location",
                disablesBases: ["ADRECALL", "USER"],
              },
              {
                value: "onlineGaming",
                label: "Online Gaming",
                disablesBases: ["ADRECALL", "USER"],
              },
              {
                value: "streamingAudioPodcast",
                label: "Streaming Audio/Podcast",
                disablesBases: ["ADRECALL", "USER"],
              },
              {
                value: "radio",
                label: "Radio",
                disablesBases: ["ADRECALL", "USER"],
              },
              {
                value: "outdoor",
                label: "Outdoor",
                disablesBases: ["ADRECALL", "USER"],
              },
              {
                value: "printMedia",
                label: "Print Media",
                disablesBases: ["ADRECALL", "USER"],
              },
              {
                value: "email",
                label: "Email",
                disablesBases: ["ADRECALL", "USER"],
              },
              {
                value: "other",
                label: "Other",
                disablesBases: ["ADRECALL", "USER"],
              },
            ],
            defaultTrendsMetric: "adRecall",
            supportVisualisationSwitch: false,
            defaultVisualisationType: "LINE",
          },
        },
        POINT_IN_TIME: {
          config: {
            apiViewMode: "POINT_IN_TIME",
            supportedBases: ["GENPOP", "FAMILIAR", "ADRECALL"],
            supportedURLKeys: [
              MARKETS,
              BRANDS,
              SEGMENTS,
              STAT_TEST,
              BASE,
              MODE,
              PERIOD_TYPE,
              PERIOD_START_DATE,
              PERIOD_END_DATE,
              VISUALIZATION_TYPE,
              VIEW_ID,
            ],
          },
          visualisationType: {
            PROFILE: {
              config: {
                supportBase: false,
                supportedURLKeys: [
                  MARKETS,
                  BRANDS,
                  SEGMENTS,
                  STAT_TEST,
                  MODE,
                  PERIOD_TYPE,
                  PERIOD_START_DATE,
                  PERIOD_END_DATE,
                  VISUALIZATION_TYPE,
                  PROFILE_GROUP,
                  PROFILE_METRIC,
                  PROFILE_RESPONSE,
                  VIEW_ID,
                ],
              },
            },
          },
        },
        PRE_POST: {
          config: {
            type: "MODE",
            supportedBases: ["GENPOP", "FAMILIAR", "ADRECALL"],
            apiViewMode: "PRE_POST",
            apiSendPrePeriod: true,
            supportPostDatePeriod: true,
            supportedURLKeys: [
              MARKETS,
              BRANDS,
              SEGMENTS,
              STAT_TEST,
              BASE,
              MODE,
              PERIOD_TYPE,
              PERIOD_START_DATE,
              PERIOD_END_DATE,
              POST_PERIOD_TYPE,
              POST_START_DATE,
              POST_END_DATE,
              VIEW_ID,
              VISUALIZATION_TYPE,
            ],
            supportVisualisationSwitch: false,
            defaultVisualisationType: "BAR",
          },
        },
      },
    },

    // ── By Brand ─────────────────────────────────────────────────────────────
    BRAND: {
      config: {
        type: "SCREEN_TYPE",
        segmentFilterModalMode: "SEGMENT_WITH_FILTERS",
        maximumAllowedMarkets: 1,
        apiGroupedBy: "BRAND",
        apiSendFilters: true,
      },
      modes: {
        TREND: {
          config: {
            apiViewMode: "TREND",
            apiSendIntervalType: true,
            supportPeriod: true,
            supportedPeriod: [
              "P_W",
              "P_M",
              "P_Q",
              "MV_1",
              "MV_2",
              "MV_4",
              "MV_8",
              "MV_12",
            ],
            supportedURLKeys: [
              MARKETS,
              BRANDS,
              SEGMENTS,
              FILTERS,
              STAT_TEST,
              BASE,
              MODE,
              PERIOD_TYPE,
              PERIOD_START_DATE,
              PERIOD_END_DATE,
              AGE_START,
              AGE_END,
              PERIOD,
              VISUALIZATION_TYPE,
              TRENDS_METRIC,
              TRENDS_RESPONSE,
              VIEW_ID,
            ],
            supportTrendsMetric: true,
            supportedTrendsMetric: [
              {
                value: "adRecall",
                label: "Advertising Recall",
                disablesBases: ["ADRECALL", "USER"],
              },
              {
                value: "tvCableSatelliteBroadcast",
                label: "TV (cable/satellite/broadcast)",
                disablesBases: ["ADRECALL", "USER"],
              },
              {
                value: "onlineStreamingVideo",
                label: "Online/Streaming Video",
                disablesBases: ["ADRECALL", "USER"],
              },
              {
                value: "newsEntertainmentWebsiteOrApp",
                label: "News/Entertainment Website or App",
                disablesBases: ["ADRECALL", "USER"],
              },
              {
                value: "eCommerceReviewWebsiteOrApp",
                label: "E-commerce/Review Website or App",
                disablesBases: ["ADRECALL", "USER"],
              },
              {
                value: "postPopUpOnSocialMedia",
                label: "Post/Pop-up on Social Media",
                disablesBases: ["ADRECALL", "USER"],
              },
              {
                value: "influencerOnSocialMedia",
                label: "Influencer on Social Media",
                disablesBases: ["ADRECALL", "USER"],
              },
              {
                value: "storePhysicalLocation",
                label: "Store/Physical Location",
                disablesBases: ["ADRECALL", "USER"],
              },
              {
                value: "onlineGaming",
                label: "Online Gaming",
                disablesBases: ["ADRECALL", "USER"],
              },
              {
                value: "streamingAudioPodcast",
                label: "Streaming Audio/Podcast",
                disablesBases: ["ADRECALL", "USER"],
              },
              {
                value: "radio",
                label: "Radio",
                disablesBases: ["ADRECALL", "USER"],
              },
              {
                value: "outdoor",
                label: "Outdoor",
                disablesBases: ["ADRECALL", "USER"],
              },
              {
                value: "printMedia",
                label: "Print Media",
                disablesBases: ["ADRECALL", "USER"],
              },
              {
                value: "email",
                label: "Email",
                disablesBases: ["ADRECALL", "USER"],
              },
              {
                value: "other",
                label: "Other",
                disablesBases: ["ADRECALL", "USER"],
              },
            ],
            defaultTrendsMetric: "adRecall",
            supportVisualisationSwitch: false,
            defaultVisualisationType: "LINE",
          },
        },
        POINT_IN_TIME: {
          config: {
            apiViewMode: "POINT_IN_TIME",
            supportedBases: ["GENPOP", "FAMILIAR", "ADRECALL"],
            supportedURLKeys: [
              MARKETS,
              BRANDS,
              SEGMENTS,
              FILTERS,
              STAT_TEST,
              BASE,
              MODE,
              PERIOD_TYPE,
              PERIOD_START_DATE,
              PERIOD_END_DATE,
              VISUALIZATION_TYPE,
              AGE_START,
              AGE_END,
              VIEW_ID,
            ],
          },
          visualisationType: {
            PROFILE: {
              config: {
                supportBase: false,
                supportedURLKeys: [
                  MARKETS,
                  BRANDS,
                  SEGMENTS,
                  FILTERS,
                  STAT_TEST,
                  MODE,
                  PERIOD_TYPE,
                  PERIOD_START_DATE,
                  PERIOD_END_DATE,
                  VISUALIZATION_TYPE,
                  AGE_START,
                  AGE_END,
                  PROFILE_GROUP,
                  PROFILE_METRIC,
                  PROFILE_RESPONSE,
                  VIEW_ID,
                ],
              },
            },
          },
        },
        PRE_POST: {
          config: {
            type: "MODE",
            apiViewMode: "PRE_POST",
            apiSendPrePeriod: true,
            supportedBases: ["GENPOP", "FAMILIAR", "ADRECALL"],
            supportPostDatePeriod: true,
            maximumAllowedBrands: 1,
            supportedURLKeys: [
              MARKETS,
              BRANDS,
              SEGMENTS,
              FILTERS,
              STAT_TEST,
              BASE,
              MODE,
              PERIOD_TYPE,
              PERIOD_START_DATE,
              PERIOD_END_DATE,
              POST_PERIOD_TYPE,
              POST_START_DATE,
              POST_END_DATE,
              AGE_START,
              AGE_END,
              VIEW_ID,
              VISUALIZATION_TYPE,
            ],
            supportVisualisationSwitch: false,
            defaultVisualisationType: "BAR",
          },
        },
      },
    },

    // ── By Market ────────────────────────────────────────────────────────────
    MARKET: {
      config: {
        type: "SCREEN_TYPE",
        segmentFilterModalMode: "SEGMENT_WITH_FILTERS",
        isMarketsFilteredByBrand: true,
        isBrandsFilteredByMarket: false,
        maximumAllowedBrands: 1,
        apiGroupedBy: "MARKET",
        apiSendFilters: true,
      },
      modes: {
        TREND: {
          config: {
            apiViewMode: "TREND",
            apiSendIntervalType: true,
            supportPeriod: true,
            supportedPeriod: [
              "P_W",
              "P_M",
              "P_Q",
              "MV_1",
              "MV_2",
              "MV_4",
              "MV_8",
              "MV_12",
            ],
            supportedURLKeys: [
              MARKETS,
              BRANDS,
              SEGMENTS,
              FILTERS,
              STAT_TEST,
              BASE,
              MODE,
              PERIOD_TYPE,
              PERIOD_START_DATE,
              PERIOD_END_DATE,
              AGE_START,
              AGE_END,
              PERIOD,
              VISUALIZATION_TYPE,
              TRENDS_METRIC,
              TRENDS_RESPONSE,
              VIEW_ID,
            ],
            supportTrendsMetric: true,
            supportedTrendsMetric: [
              {
                value: "adRecall",
                label: "Advertising Recall",
                disablesBases: ["ADRECALL", "USER"],
              },
              {
                value: "tvCableSatelliteBroadcast",
                label: "TV (cable/satellite/broadcast)",
                disablesBases: ["ADRECALL", "USER"],
              },
              {
                value: "onlineStreamingVideo",
                label: "Online/Streaming Video",
                disablesBases: ["ADRECALL", "USER"],
              },
              {
                value: "newsEntertainmentWebsiteOrApp",
                label: "News/Entertainment Website or App",
                disablesBases: ["ADRECALL", "USER"],
              },
              {
                value: "eCommerceReviewWebsiteOrApp",
                label: "E-commerce/Review Website or App",
                disablesBases: ["ADRECALL", "USER"],
              },
              {
                value: "postPopUpOnSocialMedia",
                label: "Post/Pop-up on Social Media",
                disablesBases: ["ADRECALL", "USER"],
              },
              {
                value: "influencerOnSocialMedia",
                label: "Influencer on Social Media",
                disablesBases: ["ADRECALL", "USER"],
              },
              {
                value: "storePhysicalLocation",
                label: "Store/Physical Location",
                disablesBases: ["ADRECALL", "USER"],
              },
              {
                value: "onlineGaming",
                label: "Online Gaming",
                disablesBases: ["ADRECALL", "USER"],
              },
              {
                value: "streamingAudioPodcast",
                label: "Streaming Audio/Podcast",
                disablesBases: ["ADRECALL", "USER"],
              },
              {
                value: "radio",
                label: "Radio",
                disablesBases: ["ADRECALL", "USER"],
              },
              {
                value: "outdoor",
                label: "Outdoor",
                disablesBases: ["ADRECALL", "USER"],
              },
              {
                value: "printMedia",
                label: "Print Media",
                disablesBases: ["ADRECALL", "USER"],
              },
              {
                value: "email",
                label: "Email",
                disablesBases: ["ADRECALL", "USER"],
              },
              {
                value: "other",
                label: "Other",
                disablesBases: ["ADRECALL", "USER"],
              },
            ],
            defaultTrendsMetric: "adRecall",
            supportVisualisationSwitch: false,
            defaultVisualisationType: "LINE",
          },
        },
        POINT_IN_TIME: {
          config: {
            supportedBases: ["GENPOP", "FAMILIAR", "ADRECALL"],
            apiViewMode: "POINT_IN_TIME",
            supportedURLKeys: [
              MARKETS,
              BRANDS,
              SEGMENTS,
              FILTERS,
              STAT_TEST,
              BASE,
              MODE,
              PERIOD_TYPE,
              PERIOD_START_DATE,
              PERIOD_END_DATE,
              VISUALIZATION_TYPE,
              AGE_START,
              AGE_END,
              VIEW_ID,
            ],
          },
          visualisationType: {
            PROFILE: {
              config: {
                supportBase: false,
                supportedURLKeys: [
                  MARKETS,
                  BRANDS,
                  SEGMENTS,
                  FILTERS,
                  STAT_TEST,
                  MODE,
                  PERIOD_TYPE,
                  PERIOD_START_DATE,
                  PERIOD_END_DATE,
                  VISUALIZATION_TYPE,
                  AGE_START,
                  AGE_END,
                  PROFILE_GROUP,
                  PROFILE_METRIC,
                  PROFILE_RESPONSE,
                  VIEW_ID,
                ],
              },
            },
          },
        },
        PRE_POST: {
          config: {
            type: "MODE",
            supportedBases: ["GENPOP", "FAMILIAR", "ADRECALL"],
            apiViewMode: "PRE_POST",
            apiSendPrePeriod: true,
            supportPostDatePeriod: true,
            supportedURLKeys: [
              MARKETS,
              BRANDS,
              SEGMENTS,
              FILTERS,
              STAT_TEST,
              BASE,
              MODE,
              PERIOD_TYPE,
              PERIOD_START_DATE,
              PERIOD_END_DATE,
              VISUALIZATION_TYPE,
              AGE_START,
              AGE_END,
              VIEW_ID,
              POST_PERIOD_TYPE,
              POST_START_DATE,
              POST_END_DATE,
            ],
            supportVisualisationSwitch: false,
            defaultVisualisationType: "BAR",
          },
        },
      },
    },
  },
};
